import { Link } from 'react-router'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { useTranslation } from 'react-i18next'

import {
  useFetchLastTicketsQuery,
} from 'api/dashboard'

import * as sessionUserSelector from 'selectors/sessionUser'

import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import TableCell from 'components/Shared/TableNew/TableCell'

import PartnersList from 'components/Shared/PartnersList/Index'
import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import { RouteTicketingSystemsTickets, RouteTicketingSystemsTicket } from 'routes'
import Tooltip from 'components/Shared/Tooltip/Index'
import IconLink from 'components/Shared/IconLink/Index'
import colors from 'components/utils/colors'
import apiAccessTypes from 'constants/apiAccessTypes'

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 400px;
  flex-direction: column;
  padding: 10px 16px 10px 16px;
  background-color: #FFFFFF;
  box-sizing: border-box;
  border-radius: 3px;
`
const StyledTooltip = styled(IconLink)`
  margin-right: 10px;
`

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Exo;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.secondary20};
  margin-bottom: 15px;
`

const TicketsTable = ({ ticketingSystem }) => {
  const { t } = useTranslation()

  const { data, isFetching } =
    useFetchLastTicketsQuery({
      ticketingSystemDisplayId: ticketingSystem?.displayId,
    })

  const ticketsList = data?.tickets || []

  const tooltipText = t('dashboardPage.recentTicketsTooltip')
  const tooltipId = 'create-closed-tooltip'
  const tenantTicketIdFieldName = useSelector(sessionUserSelector.tenantTicketIdFieldName)

  return (
    <StyledWrapper hasApiTable={ticketingSystem.apiAccessDisplayName !== apiAccessTypes.API_NO_ACCESS} data-test='recent-tickets-table'>
      <LoadingIndicator loading={isFetching} small />
      <Title>
        {t('dashboardPage.recentTicketsHeader')}
        <div>
          <>
            <StyledTooltip
              data-tooltip-content={tooltipText}
              data-tooltip-id={tooltipId}
              iconName='icon-help-16'
            />
            <Tooltip id='recent-tickets-tooltip' />
          </>
          <Link to={RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ticketingSystem.displayId })}>
            {t('dashboardPage.moreLink')}
          </Link>
        </div>
      </Title>

      <Table width='100%'>
        <TableHead>
          <TableRow>
            <TableCell>{t('ticketsPage.tableHeaderId')}</TableCell>
            {ticketingSystem.ticketBonding === 'enabled' &&
              <TableCell>{tenantTicketIdFieldName.replaceAll('_', ' ')}</TableCell>
            }
            <TableCell>{t('ticketsPage.tableHeaderTitle')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody clickable={false} hoverable={false}>
          {ticketsList.map(ticket => {
            const ticketUrl = RouteTicketingSystemsTicket({
              ticketingSystemDisplayId: ticketingSystem.displayId,
              ticketId: ticket.externalId,
            })

            return (
              <TableRow key={ticket.id}>
                <TableCell>
                  {ticket.externalId}
                </TableCell>
                {ticketingSystem.ticketBonding === 'enabled' && (
                  <TableCell>
                    <PartnersList partnerIds={ticket.partnerId} defaultValue='' />
                  </TableCell>
                )}
                <TableCell>
                  <Link to={ticketUrl}>{ticket.title}</Link>
                </TableCell>
              </TableRow>
            )
          })}
          <TableRowNoData data={ticketsList} />
        </TableBody>
      </Table>
    </StyledWrapper>
  )
}

TicketsTable.propTypes = {
  ticketingSystem: PropTypes.object,
}

export default TicketsTable
